import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Drawer } from 'vaul';
import { AnimatePresence, motion } from 'framer-motion';
import type { LyricLine } from '@applemusic-like-lyrics/lyric';
import {
  Fullscreen,
  ListMusic,
  MessageSquareQuote,
  Pause,
  Play,
  Repeat,
  Repeat1,
  Redo2,
  Shuffle,
  SkipBack,
  SkipForward,
  SquareSquare,
  Undo2,
} from 'lucide-react';
import { artworkUrl, fetchRelease } from '@/lib/api';
import { formatRadioDelay } from '@/lib/radioSchedule';
import { formatDuration, formatTrackNumber, type Release } from '@/types';
import { ReleaseInfoButton } from '@/components/list/ReleaseInfoPopover';
import { NowPlayingShareButton } from '@/components/player/NowPlayingShareButton';
import { ScrollingTitle } from '@/components/ui/ScrollingTitle';
import { SeekRange } from '@/components/ui/SeekRange';
import { usePlayer } from '@/hooks/usePlayer';
import {
  NowPlayingVideo,
  type NowPlayingVideoHandle,
} from '@/components/player/NowPlayingVideo';
import type { VideoPlaylistItem } from '@/components/player/VideoOverlay';
import { resolveTrackArtist } from '@/lib/artist';
import {
  lockLandscapeOrientation,
  restorePortraitOrientation,
  setMobileVideoOrientation,
} from '@/lib/mobileOrientation';

const LyricPlayer = lazy(async () => {
  await import('@applemusic-like-lyrics/core/style.css');
  const mod = await import('@applemusic-like-lyrics/react');
  return { default: mod.LyricPlayer };
});

type NowPlayingTrayProps = {
  visible: boolean;
  /** Mobile: music video plays in the artwork slot of this same drawer. */
  video?: VideoPlaylistItem | null;
  videoPlaylist?: VideoPlaylistItem[];
  onSelectVideo?: (item: VideoPlaylistItem) => void;
  onCloseVideo?: () => void;
};

type SheetFace = 'cover' | 'lyrics' | 'queue';

/** Top radius while dismissing — larger than the mini tray so close feels pillowed. */
const SHEET_CLOSE_RADIUS_PX = 20;

/** Keep drawer tied to the visual viewport (Safari chrome). Standalone skips this. */
function useVisualViewportBox(enabled: boolean) {
  const [box, setBox] = useState<{ height: number; top: number } | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setBox(null);
      return;
    }
    const vv = window.visualViewport;
    const sync = () => {
      const height = vv?.height ?? window.innerHeight;
      const top = vv?.offsetTop ?? 0;
      if (!Number.isFinite(height)) {
        setBox(null);
        return;
      }
      setBox({
        height: Math.round(height),
        top: Math.round(Number.isFinite(top) ? top : 0),
      });
    };
    sync();
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    return () => {
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [enabled]);

  return box;
}

function useTimedLyrics(lyricsUrl: string | undefined, enabled: boolean) {
  const [lines, setLines] = useState<LyricLine[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'empty' | 'error'>(
    'idle',
  );

  useEffect(() => {
    if (!enabled || !lyricsUrl) {
      setLines([]);
      setStatus(lyricsUrl ? 'idle' : 'empty');
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setStatus('loading');
    setLines([]);

    void (async () => {
      try {
        const res = await fetch(lyricsUrl, { signal: controller.signal });
        if (!res.ok) throw new Error(`lyrics ${res.status}`);
        const text = await res.text();
        const { parseTTML } = await import('@applemusic-like-lyrics/lyric');
        const parsed = parseTTML(text);
        if (cancelled) return;
        const next = parsed.lines ?? [];
        setLines(next);
        setStatus(next.length ? 'ready' : 'empty');
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) {
          return;
        }
        setLines([]);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [lyricsUrl, enabled]);

  return { lines, status };
}

/**
 * Web stand-in for Apple Music mini↔full shared-element expand
 * (cf. scrollsheet trigger-morph, RN SharedAppleMusic presets).
 * One morph OR one Vaul slide — never both.
 */
function canMorphWithViewTransition(): boolean {
  return (
    typeof document !== 'undefined' &&
    typeof document.startViewTransition === 'function' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function runViewTransition(update: () => void): ViewTransition | null {
  if (!canMorphWithViewTransition()) {
    update();
    return null;
  }
  // Marks NP open/close so CSS can hide the old root (stops page flash behind morph).
  document.documentElement.dataset.npSheetVt = '1';
  const transition = document.startViewTransition(() => {
    flushSync(update);
  });
  void transition.finished.finally(() => {
    delete document.documentElement.dataset.npSheetVt;
  });
  return transition;
}

function isStandaloneDisplay(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.dataset.displayMode === 'standalone';
}

export function NowPlayingTray({
  visible,
  video = null,
  videoPlaylist = [],
  onSelectVideo,
  onCloseVideo,
}: NowPlayingTrayProps) {
  const player = usePlayer();
  const [expanded, setExpanded] = useState(false);
  const [face, setFace] = useState<SheetFace>('cover');
  /** Keep art compact until lyrics/queue finish exiting — avoids resize+drop mid-unmount. */
  const [secondaryExiting, setSecondaryExiting] = useState(false);
  const goCover = useCallback((withExit = true) => {
    setFace((prev) => {
      if (withExit && (prev === 'lyrics' || prev === 'queue')) {
        setSecondaryExiting(true);
      } else {
        setSecondaryExiting(false);
      }
      return 'cover';
    });
  }, []);
  const [isSeeking, setIsSeeking] = useState(false);
  /** Prefer shared-element VT open; class suppresses Vaul’s enter slide so only one motion runs. */
  const [vtMorph, setVtMorph] = useState(false);
  /** Suppress Vaul’s exit slide while a VT close morph runs (tap / backdrop). */
  const [vtClosing, setVtClosing] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(1);
  const [landscape, setLandscape] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(orientation: landscape)').matches
      : false,
  );
  /** Landscape cinema: tap video to reveal transport; auto-hides. */
  const [landscapeControls, setLandscapeControls] = useState(false);
  /** Fullscreen control can enter cinema without waiting for a device rotate. */
  const [cinemaForced, setCinemaForced] = useState(false);
  /** User dismissed cinema while still physically landscape — stay soft-locked. */
  const [cinemaDismissed, setCinemaDismissed] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const trayRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<NowPlayingVideoHandle | null>(null);
  /** Must stay above any early return — gating timeupdate while scrubbing. */
  const isSeekingRef = useRef(false);
  /** Cinema chrome idle hide — reset on interaction only, never on timeupdate. */
  const cinemaIdleTimerRef = useRef<number | null>(null);
  /** After the first auto-hide, keep chrome up longer so title/meta stay readable. */
  const cinemaControlsSettledRef = useRef(false);
  /** Mirrors videoCinema for setOpen/onDrag (defined later in render). */
  const videoCinemaRef = useRef(false);
  /** Sheet open — avoid playlist-advance view transitions that flash non-black chrome. */
  const expandedRef = useRef(false);
  /**
   * Playlist track swap in flight — keep playing/cinema/black veil until the
   * next <video> fires play (teardown pause must not exit cinema).
   */
  const [videoAdvancing, setVideoAdvancing] = useState(false);
  const videoAdvancingRef = useRef(false);
  /** Last physical landscape match — distinguish Expand from rotate-back. */
  const wasLandscapeRef = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(orientation: landscape)').matches
      : false,
  );
  const CINEMA_IDLE_MS_INITIAL = 5000;
  const CINEMA_IDLE_MS_READABLE = 5000;
  /**
   * Armed on drag (ref only — never setState mid-gesture or React re-renders
   * wipe Vaul’s transform / classList and the sheet jumps).
   */
  const closingFromDragRef = useRef(false);
  const closeCleanupTimerRef = useRef<number | null>(null);
  /** Tap vs swipe-down on the fixed video host (must stay above early return). */
  const videoGestureRef = useRef<{
    x: number;
    y: number;
    dragged: boolean;
    pointerId: number;
  } | null>(null);
  const titleId = useId();
  const activeTrack = player.tracks[player.currentIndex];
  const isVideoMode = Boolean(video);
  const displayTrack = video?.track ?? activeTrack;
  const radioMode = !isVideoMode && player.radioMode;
  /** Release id for share/info — live uses the song’s source catalog id. */
  const sourceReleaseId = (
    radioMode ? activeTrack?.releaseId : player.releaseId
  )?.trim() || '';
  const sourceReleaseType = (
    radioMode ? activeTrack?.releaseType : player.releaseType
  )?.trim() || 'Album';
  const [liveReleaseInfo, setLiveReleaseInfo] = useState<{
    credits?: Release['credits'];
    streamLinks?: Release['streamLinks'];
  } | null>(null);

  const clearCinemaIdleTimer = useCallback(() => {
    if (cinemaIdleTimerRef.current != null) {
      window.clearTimeout(cinemaIdleTimerRef.current);
      cinemaIdleTimerRef.current = null;
    }
  }, []);

  /** Reveal cinema chrome and schedule auto-hide (interaction only — never timeupdate). */
  const bumpCinemaControls = useCallback(() => {
    setLandscapeControls(true);
    clearCinemaIdleTimer();
    const idleMs = cinemaControlsSettledRef.current
      ? CINEMA_IDLE_MS_READABLE
      : CINEMA_IDLE_MS_INITIAL;
    cinemaIdleTimerRef.current = window.setTimeout(() => {
      setLandscapeControls(false);
      cinemaControlsSettledRef.current = true;
      cinemaIdleTimerRef.current = null;
    }, idleMs);
  }, [clearCinemaIdleTimer]);

  useEffect(
    () => () => {
      clearCinemaIdleTimer();
    },
    [clearCinemaIdleTimer],
  );

  useEffect(() => {
    if (!radioMode || !sourceReleaseId) {
      setLiveReleaseInfo(null);
      return;
    }
    let cancelled = false;
    void fetchRelease(sourceReleaseId, sourceReleaseType || 'Album')
      .then((rel) => {
        if (cancelled) return;
        setLiveReleaseInfo({
          credits: rel.credits,
          streamLinks: rel.streamLinks,
        });
      })
      .catch(() => {
        if (!cancelled) setLiveReleaseInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [radioMode, sourceReleaseId, sourceReleaseType]);
  /**
   * Single stable <video> host — never reparented (Safari reloads media on move).
   * Positioned over the sheet art slot when expanded; full-viewport in cinema.
   */
  const sheetVideoSlotRef = useRef<HTMLDivElement | null>(null);
  /** State twin of the slot ref so layout sync re-runs when the portal mounts it. */
  const [sheetVideoSlotEl, setSheetVideoSlotEl] = useState<HTMLDivElement | null>(
    null,
  );
  const [sheetSlotBox, setSheetSlotBox] = useState<CSSProperties | null>(null);
  /** Last art-slot box from sheet mode — frozen through cinema so exit morphs back. */
  const lastSheetSlotBoxRef = useRef<CSSProperties | null>(null);
  /** Host stays above the sheet while full-bleed → slot runs. */
  const [cinemaExiting, setCinemaExiting] = useState(false);
  const cinemaExitTimerRef = useRef<number | null>(null);
  const prevVideoUrlRef = useRef<string | null>(null);
  /** Sheet body — measured before portrait cinema portal so HUD baseline matches. */
  const sheetBodyRef = useRef<HTMLDivElement | null>(null);
  /**
   * Distance from viewport bottom → sheet body bottom, applied as cinema HUD
   * `bottom` so PWA (SA-top + taller art) does not jump the transport band.
   */
  const [cinemaHudPinBottom, setCinemaHudPinBottom] = useState<number | null>(
    null,
  );

  const setSheetVideoSlot = useCallback((node: HTMLDivElement | null) => {
    sheetVideoSlotRef.current = node;
    setSheetVideoSlotEl(node);
  }, []);
  // In-Safari only: visualViewport tracks browser chrome. Standalone uses CSS 100vh.
  const viewportBox = useVisualViewportBox(
    (visible || isVideoMode) && expanded && !isStandaloneDisplay(),
  );

  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    const onChange = () => {
      const next = mq.matches;
      const wasLandscape = wasLandscapeRef.current;
      wasLandscapeRef.current = next;
      setLandscape(next);
      if (!next) {
        // Back to portrait: leave native FS; keep forced portrait cinema if any.
        videoRef.current?.exitNativeFullscreen();
        setCinemaDismissed(false);
        if (!cinemaForced) {
          setLandscapeControls(false);
          clearCinemaIdleTimer();
        } else if (wasLandscape) {
          // Real landscape → portrait rotate while cinema stays forced.
          // Expand / effect re-run while already portrait must not reveal HUD.
          bumpCinemaControls();
        }
        return;
      }
      // Playing video + rotate → cinema.
      // Lift soft-lock + keep the CSS black veil (globals: data-video-playing
      // without data-np-video-landscape) until React commits cinema markers.
      if (
        isVideoMode &&
        (videoPlaying || videoAdvancingRef.current) &&
        !cinemaDismissed
      ) {
        const root = document.documentElement;
        root.dataset.videoPlaying = '1';
        root.dataset.allowLandscape = '1';
        // Do NOT set npVideoLandscape here — veil covers until cinema effect runs.
        closingFromDragRef.current = false;
        setVtClosing(false);
        setExpanded(true);
        bumpCinemaControls();
        void lockLandscapeOrientation();
        // Native FS immediately — do not wait for a React effect tick.
        void videoRef.current?.enterNativeFullscreen();
      }
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [
    isVideoMode,
    cinemaForced,
    videoPlaying,
    cinemaDismissed,
    bumpCinemaControls,
    clearCinemaIdleTimer,
  ]);

  useEffect(() => {
    setVtMorph(canMorphWithViewTransition());
  }, []);
  const lyricsUrl = !isVideoMode
    ? activeTrack?.lyricsUrl?.trim() || undefined
    : undefined;
  const hasLyrics = Boolean(lyricsUrl);
  const { lines, status: lyricsStatus } = useTimedLyrics(
    lyricsUrl,
    expanded && hasLyrics && !isVideoMode,
  );

  const setSheetRadius = useCallback((px: number) => {
    const el = sheetRef.current;
    if (!el) return;
    const r = `${Math.max(0, px)}px`;
    el.style.setProperty('--np-sheet-radius', r);
    el.style.borderRadius = px > 0 ? `${r} ${r} 0 0` : '0';
  }, []);

  const clearSheetRadiusInline = useCallback(() => {
    const el = sheetRef.current;
    if (!el) return;
    el.style.removeProperty('--np-sheet-radius');
    el.style.removeProperty('border-radius');
  }, []);

  const finishCloseCleanup = useCallback(() => {
    if (closeCleanupTimerRef.current != null) {
      window.clearTimeout(closeCleanupTimerRef.current);
      closeCleanupTimerRef.current = null;
    }
    clearSheetRadiusInline();
    setVtClosing(false);
    closingFromDragRef.current = false;
  }, [clearSheetRadiusInline]);

  /** Prefer VT.finished; fall back to Vaul’s 500ms exit so cleanup always runs. */
  const scheduleCloseCleanup = useCallback(
    (transition: ViewTransition | null) => {
      if (closeCleanupTimerRef.current != null) {
        window.clearTimeout(closeCleanupTimerRef.current);
        closeCleanupTimerRef.current = null;
      }
      if (transition?.finished) {
        void transition.finished.then(finishCloseCleanup, finishCloseCleanup);
        // Safety if finished never settles (interrupted VT).
        closeCleanupTimerRef.current = window.setTimeout(finishCloseCleanup, 800);
        return;
      }
      closeCleanupTimerRef.current = window.setTimeout(finishCloseCleanup, 520);
    },
    [finishCloseCleanup],
  );

  useEffect(
    () => () => {
      if (closeCleanupTimerRef.current != null) {
        window.clearTimeout(closeCleanupTimerRef.current);
      }
    },
    [],
  );

  /**
   * Apple Music–style open/close (web):
   * - Tap mini tray → View Transition morph into full sheet (no Vaul enter slide)
   * - Drag down → Vaul’s single slideToBottom only (no custom scale morph)
   * - Tap backdrop / Esc → View Transition morph back into mini tray
   * Never stack morph + slide (that was the double slide-down).
   */
  const setOpen = useCallback(
    (next: boolean) => {
      if (next) {
        closingFromDragRef.current = false;
        setVtClosing(false);
        clearSheetRadiusInline();
        if (closeCleanupTimerRef.current != null) {
          window.clearTimeout(closeCleanupTimerRef.current);
          closeCleanupTimerRef.current = null;
        }
        // Keep scrubber in sync — never seek/restart the element.
        const t = videoRef.current?.getCurrentTime();
        if (typeof t === 'number' && Number.isFinite(t)) setVideoTime(t);
        runViewTransition(() => setExpanded(true));
        return;
      }

      // Cinema is not a sheet — ignore drag/backdrop/esc dismiss until exit cinema.
      if (videoCinemaRef.current) {
        closingFromDragRef.current = false;
        return;
      }

      // Dismiss sheet only — video keeps playing in the mini tray (like audio).
      setSheetRadius(SHEET_CLOSE_RADIUS_PX);
      // Drag dismiss when armed during onDrag (Vaul calls onOpenChange before onRelease).
      const fromDrag = closingFromDragRef.current;
      closingFromDragRef.current = false;

      if (fromDrag) {
        // Controlled close must be sync; Vaul already owns the exit transform.
        setVtClosing(false);
        setExpanded(false);
        scheduleCloseCleanup(null);
        return;
      }

      // Shared-element collapse into the mini player (scrollsheet-style).
      setVtClosing(true);
      const transition = runViewTransition(() => setExpanded(false));
      scheduleCloseCleanup(transition);
    },
    [clearSheetRadiusInline, scheduleCloseCleanup, setSheetRadius],
  );

  /** Open sheet / reset clock only when the video URL actually changes. */
  useEffect(() => {
    if (!video) {
      prevVideoUrlRef.current = null;
      videoAdvancingRef.current = false;
      setVideoAdvancing(false);
      setVideoPlaying(false);
      setVideoTime(0);
      setVideoDuration(1);
      setLandscapeControls(false);
      setCinemaForced(false);
      setCinemaDismissed(false);
      setMobileVideoOrientation({ playing: false, cinema: false });
      return;
    }
    const url = video.url;
    const urlChanged = prevVideoUrlRef.current !== url;
    prevVideoUrlRef.current = url;
    if (!urlChanged) return;

    goCover(false);
    // Keep “playing” through HLS teardown → next manifest so cinema/black hold.
    setVideoPlaying(true);
    setVideoTime(0);
    setVideoDuration(Math.max(1, video.track.duration || 1));
    closingFromDragRef.current = false;
    setVtClosing(false);
    clearSheetRadiusInline();
    const alreadyLandscape = window.matchMedia('(orientation: landscape)').matches;
    setLandscape(alreadyLandscape);
    if (alreadyLandscape) bumpCinemaControls();
    // Playlist advance while already open: never startViewTransition — VT
    // snapshots flash site chrome (non-black) between tracks on mobile.
    if (expandedRef.current) {
      setExpanded(true);
      return;
    }
    runViewTransition(() => setExpanded(true));
  }, [video, clearSheetRadiusInline, goCover, bumpCinemaControls]);

  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);

  const onSheetDrag = useCallback(
    (_event: PointerEvent<HTMLDivElement>, percentageDragged: number) => {
      // Portrait/landscape cinema is a locked overlay — never arm sheet dismiss.
      if (videoCinemaRef.current) {
        closingFromDragRef.current = false;
        return;
      }
      const p = Math.min(1, Math.max(0, percentageDragged));
      if (p > 0) {
        // Ref only — setState here re-renders and restarts Vaul’s open animation.
        closingFromDragRef.current = true;
      }
      // Radius only — leave transform to Vaul (one continuous slide).
      setSheetRadius(p * SHEET_CLOSE_RADIUS_PX);
    },
    [setSheetRadius],
  );

  const onSheetRelease = useCallback(
    (_event: PointerEvent<HTMLDivElement>, open: boolean) => {
      if (open) {
        // Snap-back: not a dismiss — clear so a later backdrop close uses VT.
        closingFromDragRef.current = false;
        clearSheetRadiusInline();
        return;
      }
      closingFromDragRef.current = true;
      setSheetRadius(SHEET_CLOSE_RADIUS_PX);
    },
    [clearSheetRadiusInline, setSheetRadius],
  );

  useEffect(() => {
    if (!visible && !isVideoMode) {
      setExpanded(false);
      goCover(false);
      finishCloseCleanup();
    }
  }, [visible, isVideoMode, finishCloseCleanup, goCover]);

  useEffect(() => {
    if (!expanded) goCover(false);
  }, [expanded, goCover]);

  useEffect(() => {
    if (!isVideoMode) goCover(false);
  }, [activeTrack?.id, isVideoMode, goCover]);

  useEffect(() => {
    if (!hasLyrics && face === 'lyrics') goCover(true);
  }, [hasLyrics, face, goCover]);

  useEffect(() => {
    if ((player.radioMode || isVideoMode) && face === 'lyrics') goCover(true);
    if (player.radioMode && !isVideoMode && face === 'queue') goCover(true);
  }, [player.radioMode, isVideoMode, face, goCover]);

  const lyricTimeMs = useMemo(
    () => Math.max(0, Math.round(player.currentTime * 1000)),
    [player.currentTime],
  );

  const videoPlaylistIndex = video
    ? videoPlaylist.findIndex((p) => p.url === video.url)
    : -1;

  const beginVideoAdvance = useCallback(() => {
    videoAdvancingRef.current = true;
    setVideoAdvancing(true);
    setVideoPlaying(true);
  }, []);

  const goVideo = useCallback(
    (delta: number) => {
      if (!onSelectVideo || videoPlaylistIndex < 0) return;
      const next = videoPlaylist[videoPlaylistIndex + delta];
      if (!next) return;
      // Hold black/cinema across the source swap (ended/pause must not tear down).
      beginVideoAdvance();
      onSelectVideo(next);
    },
    [beginVideoAdvance, onSelectVideo, videoPlaylist, videoPlaylistIndex],
  );

  const onVideoEnded = useCallback(() => {
    if (videoPlaylistIndex >= 0 && videoPlaylistIndex < videoPlaylist.length - 1) {
      goVideo(1);
      return;
    }
    videoAdvancingRef.current = false;
    setVideoAdvancing(false);
    // End of video queue — leave the drawer/mini like stopping audio.
    onCloseVideo?.();
    setOpen(false);
  }, [
    goVideo,
    onCloseVideo,
    setOpen,
    videoPlaylist.length,
    videoPlaylistIndex,
  ]);

  /** Ignore teardown pauses while the next playlist item is loading. */
  const onVideoPlayingChange = useCallback((playing: boolean) => {
    if (!playing && videoAdvancingRef.current) return;
    if (playing) {
      videoAdvancingRef.current = false;
      setVideoAdvancing(false);
    }
    setVideoPlaying(playing);
  }, []);

  // Cinema while playback is live (tap/expand or physical landscape).
  // Stay in cinema during playlist advance even if the element briefly pauses.
  const videoCinema =
    isVideoMode &&
    (videoPlaying || videoAdvancing) &&
    !cinemaDismissed &&
    (landscape || cinemaForced);
  /** True landscape device orientation (edge-to-edge / native FS). */
  const videoLandscape = videoCinema && landscape;
  /** Portrait vertical cinema — full-bleed viewport. */
  const videoPortraitCinema = videoCinema && !landscape;

  /**
   * Pin host to the art slot in sheet mode.
   * Freeze the last good box while cinema/exiting — cinema layout distorts the
   * slot (full-bleed / padding:0), so measuring then would break the exit morph.
   */
  useLayoutEffect(() => {
    if (!isVideoMode || !expanded) {
      setSheetSlotBox(null);
      return;
    }
    if (videoCinema || cinemaExiting) return;
    const slot = sheetVideoSlotEl ?? sheetVideoSlotRef.current;
    if (!slot) return;
    const sync = () => {
      const el = sheetVideoSlotRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return;
      const box = {
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      } satisfies CSSProperties;
      lastSheetSlotBoxRef.current = box;
      setSheetSlotBox(box);
    };
    sync();
    let raf = 0;
    let frames = 0;
    const tick = () => {
      sync();
      frames += 1;
      if (frames < 24) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(sync)
        : null;
    ro?.observe(slot);
    window.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('scroll', sync);
    return () => {
      window.cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('scroll', sync);
    };
  }, [
    isVideoMode,
    expanded,
    videoCinema,
    cinemaExiting,
    face,
    sheetVideoSlotEl,
    vtMorph,
  ]);

  videoCinemaRef.current = videoCinema;

  useEffect(() => {
    if (videoCinema) return;
    clearCinemaIdleTimer();
    cinemaControlsSettledRef.current = false;
    setLandscapeControls(false);
  }, [videoCinema, clearCinemaIdleTimer]);

  useEffect(() => {
    const root = document.documentElement;
    if (videoCinema) {
      root.dataset.npVideoLandscape = landscapeControls
        ? 'controls'
        : videoLandscape
          ? 'true'
          : 'portrait';
    } else {
      delete root.dataset.npVideoLandscape;
    }
    // Soft-lock stays on until cinema is active (avoids landscape site flash).
    // Landscape-lock only when physically landscape — not forced portrait cinema.
    setMobileVideoOrientation({
      playing:
        isVideoMode &&
        (videoPlaying || videoAdvancing) &&
        !cinemaDismissed,
      cinema: videoCinema,
      lockLandscape: videoLandscape,
    });
    return () => {
      delete root.dataset.npVideoLandscape;
    };
  }, [
    videoCinema,
    videoLandscape,
    landscapeControls,
    isVideoMode,
    videoPlaying,
    videoAdvancing,
    cinemaDismissed,
  ]);

  // Vaul leaves translate3d / will-change on the drawer — that traps position:fixed
  // chrome under the z90 video. Clear it while cinema is active so the HUD stacks.
  useLayoutEffect(() => {
    const panel = sheetRef.current;
    if (!panel) return;
    if (videoCinema) {
      panel.style.setProperty('transform', 'none', 'important');
      panel.style.setProperty('will-change', 'auto', 'important');
      return () => {
        panel.style.removeProperty('transform');
        panel.style.removeProperty('will-change');
      };
    }
    panel.style.removeProperty('transform');
    panel.style.removeProperty('will-change');
    return undefined;
  }, [videoCinema]);

  // Pause while landscape → leave cinema; soft-lock reclaims without remount.
  // Skip during playlist advance (source teardown looks like a pause).
  useEffect(() => {
    if (!isVideoMode || videoPlaying || videoAdvancing) return;
    if (!landscape && !cinemaForced) return;
    setCinemaForced(false);
    setLandscapeControls(false);
    void restorePortraitOrientation();
  }, [isVideoMode, videoPlaying, videoAdvancing, landscape, cinemaForced]);

  /**
   * Capture sheet body bottom vs viewport before the portal remount so the
   * cinema HUD can pin to the same baseline (critical in standalone PWA).
   */
  const measureCinemaHudPin = useCallback(() => {
    const body = sheetBodyRef.current;
    if (!body) {
      setCinemaHudPinBottom(null);
      return;
    }
    const r = body.getBoundingClientRect();
    if (r.height < 1) {
      setCinemaHudPinBottom(null);
      return;
    }
    const vv = window.visualViewport;
    const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    setCinemaHudPinBottom(Math.max(0, Math.round(viewBottom - r.bottom)));
  }, []);

  /**
   * Darkened, centered cinema in the current orientation (portrait stays
   * vertical). Physical rotate still enters via the orientation listener.
   */
  const enterVideoCinema = useCallback(() => {
    closingFromDragRef.current = false;
    setVtClosing(false);
    // Cancel an in-flight exit morph — overlapping is-cinema + is-cinema-exiting
    // made the second portrait enter jump.
    if (cinemaExitTimerRef.current != null) {
      window.clearTimeout(cinemaExitTimerRef.current);
      cinemaExitTimerRef.current = null;
    }
    setCinemaExiting(false);
    setCinemaDismissed(false);
    setExpanded(true);
    const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    // Measure while body is still in the sheet flex column (pre-portal).
    if (!isLandscape) measureCinemaHudPin();
    else setCinemaHudPinBottom(null);
    setCinemaForced(true);
    if (isLandscape) {
      // Landscape / native FS — show chrome immediately.
      bumpCinemaControls();
    } else {
      // Portrait: morph host with HUD hidden; tap video to fade chrome in.
      clearCinemaIdleTimer();
      cinemaControlsSettledRef.current = false;
      setLandscapeControls(false);
    }
    goCover(false);
    // Expand implies intent to watch — resume if paused.
    setVideoPlaying(true);
    videoRef.current?.play();
  }, [bumpCinemaControls, clearCinemaIdleTimer, goCover, measureCinemaHudPin]);

  const exitVideoCinema = useCallback(() => {
    if (cinemaExitTimerRef.current != null) {
      window.clearTimeout(cinemaExitTimerRef.current);
    }
    setCinemaExiting(true);
    setCinemaForced(false);
    setLandscapeControls(false);
    clearCinemaIdleTimer();
    setCinemaDismissed(true);
    setCinemaHudPinBottom(null);
    videoRef.current?.exitNativeFullscreen();
    void restorePortraitOrientation();
    cinemaExitTimerRef.current = window.setTimeout(() => {
      setCinemaExiting(false);
      cinemaExitTimerRef.current = null;
    }, 520);
  }, [clearCinemaIdleTimer]);

  useEffect(() => {
    if (videoCinema || cinemaExiting) return;
    setCinemaHudPinBottom(null);
  }, [videoCinema, cinemaExiting]);

  useEffect(
    () => () => {
      if (cinemaExitTimerRef.current != null) {
        window.clearTimeout(cinemaExitTimerRef.current);
      }
    },
    [],
  );

  isSeekingRef.current = isSeeking;

  if ((!visible && !isVideoMode) || !displayTrack) return null;

  const sheetStyle = (
    viewportBox
      ? ({
          top: `${viewportBox.top}px`,
          bottom: 'auto',
          height: `${viewportBox.height}px`,
          maxHeight: `${viewportBox.height}px`,
          viewTransitionName: expanded ? 'now-playing' : 'none',
        } satisfies CSSProperties)
      : ({
          viewTransitionName: expanded ? 'now-playing' : 'none',
        } satisfies CSSProperties)
  );

  /** Mini + meta: release cover only (never poster — artworkUrl breaks stream paths). */
  const releaseArtPath =
    (isVideoMode
      ? video?.artwork?.trim() || player.artwork
      : player.artwork) || '';
  const artSrc = artworkUrl(releaseArtPath);
  // Video must not inherit the audio session artist (e.g. still "Various Artists"
  // after browsing a compilation). Track → video/release artist → Zachary Gray.
  const artistName = isVideoMode
    ? resolveTrackArtist(displayTrack.artist, video?.artist)
    : resolveTrackArtist(displayTrack.artist, player.artist);
  const duration = isVideoMode
    ? Math.max(1, videoDuration || displayTrack.duration || 1)
    : Math.max(1, displayTrack.duration || 1);
  const currentTime = isVideoMode ? videoTime : player.currentTime;
  const isPlaying = isVideoMode ? videoPlaying : player.playing;
  /** Full-size art while playing or buffering; shrink only when stopped/paused. */
  const artFullSize =
    isPlaying || (!isVideoMode && player.radioBuffering);
  /** iOS 26: lyrics/queue compact the artwork — no card flip. */
  const compact =
    !isVideoMode && (face !== 'cover' || secondaryExiting);

  const radio = radioMode;
  const radioLive = radio && player.radioTimeshiftMs <= 0;
  const radioDelayLabel = radio
    ? radioLive
      ? 'LIVE'
      : formatRadioDelay(player.radioTimeshiftMs)
    : '';
  const infoCredits = radio ? liveReleaseInfo?.credits : player.credits;
  const infoStreams = radio ? liveReleaseInfo?.streamLinks : player.streamLinks;
  const shareReleaseId = sourceReleaseId;
  const shareAlbumName = radio
    ? displayTrack.albumName || player.albumName
    : player.albumName;

  const togglePlay = (event?: MouseEvent) => {
    event?.stopPropagation();
    if (isVideoMode) {
      videoRef.current?.toggle();
      return;
    }
    if (activeTrack) player.toggleTrack(activeTrack.position);
  };

  const seekTo = (timeSec: number) => {
    if (isVideoMode) {
      videoRef.current?.seek(timeSec);
      setVideoTime(timeSec);
      return;
    }
    if (radio && radioLive) return;
    player.seek(timeSec);
  };

  const onSeekScrubbingChange = (scrubbing: boolean) => {
    if (!isVideoMode) player.setSeekScrubbing(scrubbing);
    setIsSeeking(scrubbing);
  };

  const playPrevious = () => {
    if (isVideoMode) {
      goVideo(-1);
      return;
    }
    player.playPrevious();
  };

  const playNext = () => {
    if (isVideoMode) {
      goVideo(1);
      return;
    }
    player.playNext();
  };

  const repeatLabel =
    player.repeatMode === 'one'
      ? 'Repeat one'
      : player.repeatMode === 'all'
        ? 'Repeat all'
        : 'Repeat off';

  const toggleLyrics = () => {
    if (isVideoMode || !hasLyrics) return;
    setFace((prev) => {
      if (prev === 'lyrics') {
        setSecondaryExiting(true);
        return 'cover';
      }
      setSecondaryExiting(false);
      return 'lyrics';
    });
  };

  const toggleQueue = () => {
    if (player.radioMode && !isVideoMode) return;
    setFace((prev) => {
      if (prev === 'queue') {
        setSecondaryExiting(true);
        return 'cover';
      }
      setSecondaryExiting(false);
      return 'queue';
    });
  };

  const artEase = [0.32, 0.72, 0, 1] as const;
  const canVideoPrev = isVideoMode && videoPlaylistIndex > 0;
  const canVideoNext =
    isVideoMode &&
    videoPlaylistIndex >= 0 &&
    videoPlaylistIndex < videoPlaylist.length - 1;

  /**
   * Video host sits above the Vaul drawer, so it steals vertical drags.
   * Tap → cinema (sheet) or toggle HUD (cinema). Swipe down → close Vaul
   * (and leave cinema when already expanded to fullscreen).
   */
  const onVideoHostPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!expanded || !isVideoMode) return;
    videoGestureRef.current = {
      x: e.clientX,
      y: e.clientY,
      dragged: false,
      pointerId: e.pointerId,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onVideoHostPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const g = videoGestureRef.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const dy = e.clientY - g.y;
    const dx = e.clientX - g.x;
    if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) {
      g.dragged = true;
    }
  };

  const onVideoHostPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const g = videoGestureRef.current;
    videoGestureRef.current = null;
    if (!g || g.pointerId !== e.pointerId || !expanded || !isVideoMode) return;
    const dy = e.clientY - g.y;
    if (g.dragged && dy > 48) {
      if (videoCinema) {
        exitVideoCinema();
      }
      closingFromDragRef.current = true;
      setOpen(false);
      return;
    }
    if (!g.dragged) {
      if (videoCinema) bumpCinemaControls();
      else enterVideoCinema();
    }
  };

  const onVideoHostPointerCancel = () => {
    videoGestureRef.current = null;
  };

  return (
    <Drawer.Root
      open={expanded}
      onOpenChange={setOpen}
      onDrag={onSheetDrag}
      onRelease={onSheetRelease}
      shouldScaleBackground={false}
      repositionInputs={false}
      dismissible={!videoCinema}
      closeThreshold={0.25}
    >
      {/*
        VT shell pads past the tray so soft box-shadow stays inside the
        captured snapshot (WebKit clips shadows outside the named box).
      */}
      <div
        className={`now-playing-tray-vt${expanded ? ' is-expanded-hidden' : ''}`}
        style={{
          viewTransitionName: expanded ? 'none' : 'now-playing',
        }}
      >
        <div
          ref={trayRef}
          className={`now-playing-tray${isVideoMode ? ' is-video' : ''}`}
          role="region"
          aria-label="Now playing"
        >
          <button
            type="button"
            className="now-playing-mini"
            onClick={() => setOpen(true)}
            aria-expanded={expanded}
            aria-controls={titleId}
          >
            <span className="now-playing-art" aria-hidden>
              {artSrc ? (
                <img src={artSrc} alt="" draggable={false} />
              ) : (
                <span className="now-playing-art-fallback" />
              )}
            </span>
            <span className="now-playing-meta">
              <ScrollingTitle
                text={displayTrack.name}
                className="now-playing-title"
                active={!expanded}
                resetOnViewTransition={false}
              />
              <span className="now-playing-sub">{artistName}</span>
            </span>
          </button>
          <button
            type="button"
            className="now-playing-play"
            aria-label={isPlaying ? 'Pause playback' : 'Resume playback'}
            onClick={togglePlay}
          >
            {isPlaying ? (
              <Pause size={18} fill="currentColor" />
            ) : (
              <Play size={18} fill="currentColor" />
            )}
          </button>
        </div>
      </div>

      {/* Stable video host — never reparented so playback position survives mini↔sheet. */}
      {isVideoMode && video ? (
        <div
          className={[
            'now-playing-video-host',
            expanded ? 'is-sheet' : 'is-mini',
            videoCinema ? 'is-cinema' : '',
            videoPortraitCinema ? 'is-cinema-portrait' : '',
            videoLandscape ? 'is-cinema-landscape' : '',
            cinemaExiting ? 'is-cinema-exiting' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={
            videoCinema
              ? ({
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 1,
                } satisfies CSSProperties)
              : expanded
                ? sheetSlotBox ??
                  lastSheetSlotBoxRef.current ??
                  ({
                    // Until the art-slot rect is known, stay invisible — never flash at top:0.
                    opacity: 0,
                    pointerEvents: 'none',
                    width: 1,
                    height: 1,
                    left: 0,
                    bottom: 0,
                    top: 'auto',
                  } satisfies CSSProperties)
                : undefined
          }
          onPointerDown={onVideoHostPointerDown}
          onPointerMove={onVideoHostPointerMove}
          onPointerUp={onVideoHostPointerUp}
          onPointerCancel={onVideoHostPointerCancel}
          aria-hidden={!expanded}
        >
          <NowPlayingVideo
            ref={videoRef}
            videoUrl={video.url}
            track={video.track}
            artwork={video.artwork}
            album={video.album}
            artist={video.artist}
            canPrevious={canVideoPrev}
            canNext={canVideoNext}
            onPrevious={() => goVideo(-1)}
            onNext={() => goVideo(1)}
            onTimeUpdate={(t, d) => {
              if (!isSeekingRef.current) setVideoTime(t);
              if (d > 0) setVideoDuration(d);
            }}
            onPlayingChange={onVideoPlayingChange}
            onEnded={onVideoEnded}
          />
        </div>
      ) : null}

      <Drawer.Portal>
        <Drawer.Overlay className="now-playing-backdrop" />
        <Drawer.Content
          ref={sheetRef}
          className={[
            'now-playing-sheet-panel',
            compact ? 'is-compact' : '',
            isVideoMode ? 'is-video' : '',
            videoCinema ? 'is-video-cinema' : '',
            videoPortraitCinema ? 'is-video-cinema-portrait' : '',
            videoLandscape ? 'is-video-landscape' : '',
            videoCinema && landscapeControls ? 'is-video-cinema-controls' : '',
            vtMorph ? 'is-vt-morph' : '',
            vtClosing ? 'is-vt-closing' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-labelledby={titleId}
          style={sheetStyle}
        >
          {/*
            Cinema HUD is portaled to document.body (strips only, z91) so it sits
            above the z90 video without a full-screen lid that blanks iOS <video>.
            Sheet mode keeps chrome/body inside the drawer as usual.
          */}
          {!videoCinema ? (
            <div className="now-playing-sheet-chrome">
              <Drawer.Handle className="now-playing-handle" />
            </div>
          ) : null}

          <div
            className={`now-playing-sheet-stage${compact ? ' is-compact' : ' is-balanced'}${isVideoMode ? ' is-video' : ''}`}
            aria-live="polite"
          >
            {/*
              iOS Music pattern: cover keeps art balanced between handle + meta;
              lyrics/queue shrink art into a top header with title/artist beside it.
            */}
            <div
              className={`now-playing-sheet-heading${compact ? ' is-compact' : ''}${isVideoMode ? ' is-video' : ''}`}
            >
              <div
                className={`now-playing-sheet-art-frame${isVideoMode ? ' is-video' : ''}${compact ? ' is-compact' : ''}`}
              >
                <motion.button
                  type="button"
                  className={[
                    'now-playing-sheet-art-wrap',
                    compact ? 'is-compact' : '',
                    isVideoMode ? 'is-video' : '',
                    !isVideoMode && !compact
                      ? artFullSize
                        ? 'is-playing'
                        : 'is-paused'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-label={
                    videoCinema
                      ? 'Show playback controls'
                      : isVideoMode
                        ? 'Expand video'
                        : compact
                          ? 'Show album artwork'
                          : undefined
                  }
                  disabled={!compact && !videoCinema && !isVideoMode}
                  onClick={(e) => {
                    if (videoCinema) {
                      e.stopPropagation();
                      // Tap wakes chrome; idle timer fades it after a few seconds.
                      bumpCinemaControls();
                      return;
                    }
                    if (isVideoMode) {
                      e.stopPropagation();
                      enterVideoCinema();
                      return;
                    }
                    if (compact) goCover(true);
                  }}
                  /* CSS owns the size morph from top-left; FLIP fought flex reflow. */
                  layout={false}
                >
                  <motion.div
                    className="now-playing-sheet-art"
                    layout={false}
                    animate={
                      !isVideoMode && !compact
                        ? { scale: artFullSize ? 1 : 0.88 }
                        : { scale: 1 }
                    }
                    transition={{ duration: 0.45, ease: artEase }}
                  >
                    {isVideoMode ? (
                      /* Black slot only — live video paints in the fixed host overlay. */
                      <div
                        className="now-playing-sheet-video-slot"
                        ref={setSheetVideoSlot}
                        aria-hidden
                      />
                    ) : artSrc ? (
                      <img src={artSrc} alt="" draggable={false} />
                    ) : (
                      <span className="now-playing-art-fallback large" />
                    )}
                  </motion.div>
                </motion.button>
              </div>

              {/* Audio: meta sits under art (cover) or beside compact art (lyrics/queue).
                  Video keeps meta in the body so landscape chrome can overlay it. */}
              {!isVideoMode ? (
                <div className="now-playing-sheet-meta">
                  <div className="now-playing-sheet-meta-text">
                    <div className="now-playing-sheet-title-row">
                      <Drawer.Title
                        id={titleId}
                        className="now-playing-sheet-title"
                      >
                        <ScrollingTitle text={displayTrack.name} />
                      </Drawer.Title>
                      <div className="now-playing-sheet-meta-actions">
                        <NowPlayingShareButton
                          trackName={displayTrack.name}
                          artistName={artistName}
                          releaseId={shareReleaseId}
                          trackPosition={displayTrack.position}
                          albumName={shareAlbumName}
                          artwork={player.artwork || artSrc}
                          className="now-playing-sheet-share"
                        />
                        <ReleaseInfoButton
                          credits={infoCredits}
                          streamLinks={infoStreams}
                          trackPosition={displayTrack.position}
                          className="now-playing-sheet-info"
                          side="top"
                          align="end"
                        />
                      </div>
                    </div>
                    <Drawer.Description className="now-playing-sheet-sub">
                      {artistName}
                    </Drawer.Description>
                  </div>
                </div>
              ) : null}
            </div>

            <AnimatePresence
              mode="wait"
              initial={false}
              onExitComplete={() => setSecondaryExiting(false)}
            >
              {face === 'lyrics' ? (
                <motion.div
                  key="lyrics"
                  className="now-playing-sheet-secondary now-playing-sheet-lyrics-face"
                  initial={{ opacity: 0, y: 28, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 16, scale: 0.99 }}
                  transition={{ duration: 0.4, ease: artEase }}
                >
                  {lyricsStatus === 'ready' ? (
                    <Suspense
                      fallback={
                        <p className="now-playing-sheet-face-empty">
                          Loading lyrics…
                        </p>
                      }
                    >
                      <LyricPlayer
                        className="now-playing-lyric-player"
                        lyricLines={lines}
                        currentTime={lyricTimeMs}
                        isSeeking={isSeeking}
                        playing={player.playing}
                        enableSpring={false}
                        alignAnchor="center"
                        alignPosition={0.35}
                        onLyricLineClick={(evt) => {
                          const startMs = evt.line.getLine().startTime;
                          if (Number.isFinite(startMs)) seekTo(startMs / 1000);
                        }}
                      />
                    </Suspense>
                  ) : (
                    <p className="now-playing-sheet-face-empty">
                      {lyricsStatus === 'loading'
                        ? 'Loading lyrics…'
                        : 'Lyrics aren’t available for this track.'}
                    </p>
                  )}
                </motion.div>
              ) : null}

              {face === 'queue' ? (
                <motion.div
                  key="queue"
                  className="now-playing-sheet-secondary now-playing-sheet-queue-face"
                  role="list"
                  initial={{ opacity: 0, y: 28, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 16, scale: 0.99 }}
                  transition={{ duration: 0.4, ease: artEase }}
                >
                  {isVideoMode
                    ? videoPlaylist.map((item, index) => {
                        const active = index === videoPlaylistIndex;
                        return (
                          <button
                            key={`${item.track.id}-video`}
                            type="button"
                            role="listitem"
                            className={`now-playing-queue-row${active ? ' is-active' : ''}`}
                            onClick={() => onSelectVideo?.(item)}
                          >
                            <span className="now-playing-queue-no">
                              {formatTrackNumber(index + 1)}
                            </span>
                            <span className="now-playing-queue-name">
                              {item.track.name}
                            </span>
                            <span className="now-playing-queue-time">
                              {formatDuration(item.track.duration || 0)}
                            </span>
                          </button>
                        );
                      })
                    : player.tracks.map((track, index) => {
                        const active = index === player.currentIndex;
                        const playable =
                          track.duration !== 0 &&
                          Boolean(track.streamUrl?.trim());
                        return (
                          <button
                            key={track.id}
                            type="button"
                            role="listitem"
                            className={`now-playing-queue-row${active ? ' is-active' : ''}`}
                            disabled={!playable}
                            onClick={() => {
                              if (playable) player.toggleTrack(track.position);
                            }}
                          >
                            <span className="now-playing-queue-no">
                              {formatTrackNumber(track.position)}
                            </span>
                            <span className="now-playing-queue-name">
                              {track.name}
                            </span>
                            <span className="now-playing-queue-time">
                              {formatDuration(track.duration || 0)}
                            </span>
                          </button>
                        );
                      })}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          {(() => {
            const cinemaPinned =
              videoPortraitCinema && cinemaHudPinBottom != null;
            const cinemaPinStyle = cinemaPinned
              ? ({
                  ['--np-cinema-pin-bottom']: `${cinemaHudPinBottom}px`,
                } as CSSProperties)
              : undefined;
            const sheetBody = (
              <div
                ref={sheetBodyRef}
                className={[
                  'now-playing-sheet-body',
                  videoCinema ? 'now-playing-cinema-body' : '',
                  cinemaPinned ? 'is-pinned' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={cinemaPinStyle}
                onClick={(e) => {
                  if (videoCinema) e.stopPropagation();
                }}
                onPointerDown={() => {
                  if (videoCinema) bumpCinemaControls();
                }}
              >
            {isVideoMode ? (
              <div className="now-playing-sheet-meta">
                <div className="now-playing-sheet-meta-text">
                  <div className="now-playing-sheet-title-row">
                    <Drawer.Title
                      id={titleId}
                      className="now-playing-sheet-title"
                    >
                      <ScrollingTitle text={displayTrack.name} />
                    </Drawer.Title>
                    <div className="now-playing-sheet-meta-actions">
                      <NowPlayingShareButton
                        trackName={displayTrack.name}
                        artistName={artistName}
                        releaseId={shareReleaseId}
                        trackPosition={displayTrack.position}
                        albumName={shareAlbumName}
                        artwork={player.artwork || artSrc}
                        className="now-playing-sheet-share"
                      />
                      <ReleaseInfoButton
                        credits={infoCredits}
                        streamLinks={infoStreams}
                        trackPosition={displayTrack.position}
                        className="now-playing-sheet-info"
                        side="top"
                        align="end"
                      />
                    </div>
                  </div>
                  <Drawer.Description className="now-playing-sheet-sub">
                    {artistName}
                  </Drawer.Description>
                </div>
              </div>
            ) : null}

            {radio ? (
              <div
                className={[
                  'live-progress',
                  player.radioBuffering
                    ? 'is-buffering'
                    : player.playing && radioLive
                      ? 'is-playing'
                      : '',
                  !player.radioBuffering && !radioLive ? 'is-timeshift' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="status"
                aria-label={
                  player.radioBuffering
                    ? 'Buffering live radio'
                    : radioLive
                      ? 'Live'
                      : `Behind live ${radioDelayLabel}`
                }
                aria-busy={player.radioBuffering || undefined}
              >
                <div className="live-progress-track">
                  <div className="live-progress-bar" aria-hidden />
                  <button
                    type="button"
                    className={`live-progress-live${
                      player.radioBuffering
                        ? ' is-buffering'
                        : radioLive
                          ? ''
                          : ' is-behind'
                    }`}
                    aria-label={
                      player.radioBuffering
                        ? 'Buffering'
                        : radioLive
                          ? 'On live'
                          : 'Jump to live'
                    }
                    disabled={radioLive || player.radioBuffering}
                    onClick={() => player.jumpToLive()}
                  >
                    <span className="live-progress-live-text" key={player.radioBuffering ? 'buf' : 'live'}>
                      {player.radioBuffering ? 'BUFFERING' : 'LIVE'}
                    </span>
                  </button>
                </div>
                {!player.radioBuffering && !radioLive ? (
                  <div className="live-progress-times">
                    <span className="live-progress-label is-behind">{radioDelayLabel}</span>
                    <span aria-hidden />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="now-playing-sheet-seek">
                <SeekRange
                  className="seek"
                  aria-label={isVideoMode ? 'Seek video' : 'Seek audio'}
                  min={0}
                  max={duration}
                  step={0.1}
                  value={currentTime}
                  liveSeek={!isVideoMode}
                  onScrubbingChange={onSeekScrubbingChange}
                  onSeek={seekTo}
                />
                <div className="now-playing-sheet-times">
                  <span className="player-time">
                    {formatDuration(currentTime)}
                  </span>
                  <span className="player-time player-duration">
                    -{formatDuration(Math.max(0, duration - currentTime))}
                  </span>
                </div>
              </div>
            )}

            {/* Apple Music–style: large centered transport only */}
            <div className={`now-playing-sheet-controls${radio ? ' is-live' : ''}`}>
              <button
                type="button"
                className="now-playing-sheet-skip"
                aria-label={
                  isVideoMode
                    ? 'Previous video'
                    : radio
                      ? 'Skip back 15 seconds'
                      : 'Previous track'
                }
                disabled={isVideoMode ? !canVideoPrev : false}
                onClick={playPrevious}
              >
                {radio ? (
                  <Undo2 size={30} strokeWidth={2.25} />
                ) : (
                  <SkipBack size={32} fill="currentColor" />
                )}
              </button>
              <button
                type="button"
                className="now-playing-sheet-play"
                aria-label={
                  isPlaying
                    ? isVideoMode
                      ? 'Pause video'
                      : radio
                        ? 'Pause radio'
                        : 'Pause playback'
                    : isVideoMode
                      ? 'Resume video'
                      : radio
                        ? radioLive
                          ? 'Resume live radio'
                          : 'Resume radio'
                        : 'Resume playback'
                }
                onClick={() => togglePlay()}
              >
                {isPlaying ? (
                  <Pause size={40} fill="currentColor" />
                ) : (
                  <Play size={40} fill="currentColor" />
                )}
              </button>
              <button
                type="button"
                className="now-playing-sheet-skip"
                aria-label={
                  isVideoMode
                    ? 'Next video'
                    : radio
                      ? radioLive
                        ? 'Already live'
                        : 'Skip forward 15 seconds toward live'
                      : 'Next track'
                }
                disabled={
                  isVideoMode ? !canVideoNext : Boolean(radio && radioLive)
                }
                onClick={playNext}
              >
                {radio ? (
                  <Redo2 size={30} strokeWidth={2.25} />
                ) : (
                  <SkipForward size={32} fill="currentColor" />
                )}
              </button>
            </div>

            {/* Bottom utility row: lyrics|expand · shuffle/repeat · queue */}
            <div className="now-playing-sheet-extras">
              {isVideoMode ? (
                <button
                  type="button"
                  className={`now-playing-sheet-icon-btn${videoCinema ? ' is-active' : ''}`}
                  aria-label={
                    videoCinema ? 'Fit video to frame' : 'Expand video'
                  }
                  aria-pressed={videoCinema}
                  onClick={videoCinema ? exitVideoCinema : enterVideoCinema}
                >
                  {videoCinema ? (
                    <Fullscreen size={22} strokeWidth={2} />
                  ) : (
                    <SquareSquare size={22} strokeWidth={2} />
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className={`now-playing-sheet-icon-btn${face === 'lyrics' ? ' is-active' : ''}${hasLyrics ? '' : ' is-disabled'}`}
                  aria-label={hasLyrics ? 'Lyrics' : 'Lyrics unavailable'}
                  aria-pressed={face === 'lyrics'}
                  aria-disabled={!hasLyrics}
                  disabled={!hasLyrics}
                  onClick={toggleLyrics}
                >
                  <MessageSquareQuote size={22} />
                </button>
              )}
              {!radio && !isVideoMode ? (
                <div className="now-playing-sheet-extras-mid">
                  <button
                    type="button"
                    className={`now-playing-sheet-icon-btn${player.shuffleMode ? ' is-active' : ''}`}
                    aria-label={player.shuffleMode ? 'Shuffle on' : 'Shuffle off'}
                    aria-pressed={player.shuffleMode}
                    onClick={player.toggleShuffle}
                  >
                    <Shuffle size={20} />
                  </button>
                  <button
                    type="button"
                    className={`now-playing-sheet-icon-btn${player.repeatMode !== 'off' ? ' is-active' : ''}`}
                    aria-label={repeatLabel}
                    aria-pressed={player.repeatMode !== 'off'}
                    onClick={player.cycleRepeat}
                  >
                    {player.repeatMode === 'one' ? (
                      <Repeat1 size={20} />
                    ) : (
                      <Repeat size={20} />
                    )}
                  </button>
                </div>
              ) : (
                <span className="now-playing-sheet-extras-mid" aria-hidden />
              )}
              <button
                type="button"
                className={`now-playing-sheet-icon-btn${face === 'queue' ? ' is-active' : ''}${radio && !isVideoMode ? ' is-disabled' : ''}`}
                aria-label={
                  isVideoMode
                    ? 'Video queue'
                    : radio
                      ? 'Up next unavailable in live mode'
                      : 'Up next'
                }
                aria-pressed={face === 'queue'}
                aria-disabled={radio && !isVideoMode}
                disabled={radio && !isVideoMode}
                onClick={toggleQueue}
              >
                <ListMusic size={22} />
              </button>
            </div>
              </div>
            );
            if (!videoCinema) return sheetBody;
            // Strips only at z91 — no full-screen parent (that blanks iOS video).
            return createPortal(
              <div
                className={`now-playing-cinema-hud${landscapeControls ? ' is-visible' : ''}`}
              >
                {sheetBody}
              </div>,
              document.body,
            );
          })()}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
